import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import theme, { mobileBreakpoint } from '../theme';
import PageLayout from './PageLayout';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import { PaginationContainer, PaginationButton, PaginationInfo, PackageBadgeWithIcon } from './shared/CommonElements';
import { getBackNavigationPath, getBackButtonText, isUserAdmin } from '../utils/adminUtils';
import ConfirmationPopup from './ConfirmationPopup';

const HeroSection = styled.section`
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin: 40px 0 0 0;
  margin-bottom: 16px;
`;

const HeroTitle = styled.div`
  display: flex;
  margin-bottom: 16px;
  align-items: center;
  white-space: normal;
  word-break: break-word;
`;



const MainTitle = styled.h1`
  font-size: 64px;
  z-index: 1000;
  margin: 0;
  line-height: 1;
  color: ${props => props.theme.colors.primary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 38px;
  }
`;

const Subtitle = styled.h2`
  font-size: 32px;
  z-index: 1000;
  font-style: italic;
  margin: 0 0 24px 0;
  color: ${props => props.theme.colors.secondary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 24px;
  }
`;

const ContentContainer = styled.div`
  max-width: 800px;
  z-index: 1000;
  margin: 0 auto;
  padding: 40px 24px 48px 24px;
  background: linear-gradient(135deg, #fff 0%, #f8f9ff 100%);
  border-radius: ${theme.borderRadius.large};
  box-shadow: 0 8px 32px rgba(0,0,0,0.08);
  color: ${theme.colors.primary};
`;

const PackageCard = styled.div`
  background: #f5f5f5;
  border-radius: ${theme.borderRadius.medium};
  border: 1px solid #e0e0e0;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  }
`;

const PackageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
`;

const PackageName = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${theme.colors.primary};
  margin: 0;
`;

const PackagePrice = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${theme.colors.secondary};

  border-radius: ${theme.borderRadius.small};
`;

const PackageDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLabel = styled.span`
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
`;

const DetailValue = styled.span`
  font-size: 1.1rem;
  color: ${theme.colors.primary};
  font-weight: 600;
  word-break: break-all;
  overflow-wrap: break-word;
`;

const LessonsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-top: 16px;
`;

const LessonTypeCard = styled.div`
  background: ${props => {
    if (props.available === 0) {
      return '#f5f5f5';
    }
    switch (props.category) {
      case 'CHILDREN':
        return 'rgba(188, 231, 253, 0.13)';
      case 'ADULTS':
        return 'rgba(196, 146, 177, 0.08)';
      case 'SPECIAL':
        return 'rgba(219, 39, 98, 0.08)';
      default:
        return '#f5f5f5';
    }
  }};
  border: 1px solid ${props => {
    if (props.available === 0) {
      return '#ddd';
    }
    switch (props.category) {
      case 'CHILDREN':
        return '#bce7fd';
      case 'ADULTS':
        return '#c492b1';
      case 'SPECIAL':
        return '#db2762';
      default:
        return '#ddd';
    }
  }};
  border-radius: ${theme.borderRadius.small};
  padding: 12px;
  text-align: center;
`;

const LessonTypeName = styled.div`
  font-size: 0.9rem;
  color: ${props => props.available > 0 ? props.primaryColor : '#666'};
  margin-bottom: 4px;
`;

const LessonTypeAmount = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${props => props.available > 0 ? props.primaryColor : '#999'};
`;

const StatusBadge = styled.div`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => props.status === 'active' ? '#e8f5e8' : '#fff3cd'};
  color: ${props => props.status === 'active' ? '#2e7d32' : '#856404'};
  border: 1px solid ${props => props.status === 'active' ? '#4CAF50' : '#ffeaa7'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
  opacity: 0.5;
`;

const EmptyStateText = styled.p`
  font-size: 1.2rem;
  margin-bottom: 20px;
`;

const BackButton = styled.button`
  padding: 12px 24px;
  background: #fff;
  color: ${theme.colors.primary};
  border: 2px solid ${theme.colors.accent1};
  border-radius: ${theme.borderRadius.medium};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 20px;
  position: relative;
  
  &:hover {
    background: ${theme.colors.accent1};
    color: white;
  }
`;

const DownloadButton = styled.button`
  padding: 8px 16px;
  background: ${theme.colors.secondary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.small};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    background: #e8316d;
    transform: scale(1.02);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const CancelButton = styled.button`
  padding: 8px 16px;
  background: ${theme.colors.secondary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.small};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    background: #e8316d;
    transform: scale(1.02);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const SkeletonCard = styled.div`
  background: #f5f5f5;
  border-radius: ${theme.borderRadius.medium};
  border: 1px solid #e0e0e0;
  padding: 24px;
  margin-bottom: 20px;
  animation: pulse 1.5s ease-in-out infinite;
  
  @keyframes pulse {
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
  }
`;

const SkeletonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
`;

const SkeletonTitle = styled.div`
  height: 24px;
  width: 200px;
  background: #e0e0e0;
  border-radius: 4px;
`;

const SkeletonPrice = styled.div`
  height: 20px;
  width: 100px;
  background: #e0e0e0;
  border-radius: 4px;
`;

const SkeletonDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SkeletonDetailItem = styled.div`
  height: 16px;
  width: 100%;
  background: #e0e0e0;
  border-radius: 4px;
`;

const SkeletonLessonsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-top: 16px;
`;

const SkeletonLessonCard = styled.div`
  height: 60px;
  background: #e0e0e0;
  border-radius: ${theme.borderRadius.small};
`;

const PurchasedPackagesPage = () => {
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [lessonTypes, setLessonTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState({});
  const [cancelling, setCancelling] = useState({});
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const packagesPerPage = 5;

  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated() || !token) return;
    
      // Small delay to ensure auth state is stable
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        // Fetch lesson types first
        const lessonTypesResponse = await fetch(config.getApiUrl('/api/lesson-types'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (lessonTypesResponse.ok) {
          const lessonTypesData = await lessonTypesResponse.json();
          setLessonTypes(lessonTypesData.lessonTypes || []);
        }

        // Fetch purchased packages
        const packagesResponse = await fetch(config.getApiUrl('/api/purchase/purchased-packages'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (packagesResponse.ok) {
          const packagesData = await packagesResponse.json();
          setPackages(packagesData.packages || []);
        } else {
          setError('Fehler beim Laden der gekauften Pakete');
        }
      } catch (err) {
        setError('Verbindungsfehler');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, isAuthenticated, navigate]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getPackageStatus = (purchasedPackage) => {
    const purchaseDate = new Date(purchasedPackage.timestamp);
    const expiryDate = new Date(purchaseDate.getTime() + (365 * 24 * 60 * 60 * 1000)); // 1 year
    const now = new Date();
    
    if (now > expiryDate) {
      return { status: 'expired', text: 'Abgelaufen' };
    }
    
    const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    if (daysUntilExpiry <= 30) {
      return { status: 'warning', text: `Läuft in ${daysUntilExpiry} Tagen ab` };
    }
    
    return { status: 'active', text: 'Aktiv' };
  };

  const getTotalAvailableLessons = (availableBudgets) => {
    return Object.values(availableBudgets).reduce((sum, budget) => sum + parseInt(budget || 0), 0);
  };

  const getLessonTypeName = (lessonTypeId) => {
    const lessonType = lessonTypes.find(lt => lt.id === lessonTypeId);
    return lessonType ? lessonType.name : lessonTypeId;
  };

  const getLessonCategory = (lessonTypeId) => {
    const lessonType = lessonTypes.find(lt => lt.id === lessonTypeId);
    return lessonType ? lessonType.category : 'OTHER';
  };

  const renderSkeletonCards = () => {
    return Array.from({ length: 3 }, (_, index) => (
      <SkeletonCard key={index}>
        <SkeletonHeader>
          <SkeletonTitle />
          <SkeletonPrice />
        </SkeletonHeader>
        <SkeletonDetails>
          <SkeletonDetailItem />
          <SkeletonDetailItem />
          <SkeletonDetailItem />
        </SkeletonDetails>
        <SkeletonLessonsGrid>
          <SkeletonLessonCard />
          <SkeletonLessonCard />
          <SkeletonLessonCard />
        </SkeletonLessonsGrid>
      </SkeletonCard>
    ));
  };

  // Pagination logic
  const totalPages = Math.ceil(packages.length / packagesPerPage);
  const startIndex = (currentPage - 1) * packagesPerPage;
  const endIndex = startIndex + packagesPerPage;
  const currentPackages = packages.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const downloadInvoice = async (packageId, isAnnulmentInvoice = false) => {
    if (!isAuthenticated() || !token) return;

    const downloadKey = isAnnulmentInvoice ? `${packageId}_annulment` : packageId;
    setDownloading(prev => ({ ...prev, [downloadKey]: true }));

    try {
      const url = isAnnulmentInvoice 
        ? config.getApiUrl(`/api/purchase/download-annulment-invoice/${packageId}`)
        : config.getApiUrl(`/api/purchase/download-invoice/${packageId}`);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const urlObj = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlObj;
        
        // Extract filename from Content-Disposition header, fallback to default
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = isAnnulmentInvoice ? 'Stornorechnung.pdf' : 'Rechnung.pdf';
        if (contentDisposition) {
          // Handle both quoted and unquoted filenames (case-insensitive)
          // Try quoted format first: filename="..." (most common from backend)
          const quotedMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"/i);
          if (quotedMatch && quotedMatch[1]) {
            filename = quotedMatch[1];
            console.log('Extracted filename from quoted match:', filename);
          } else {
            // Try unquoted format: filename=...
            const unquotedMatch = contentDisposition.match(/filename\s*=\s*([^;\s]+)/i);
            if (unquotedMatch && unquotedMatch[1]) {
              filename = unquotedMatch[1].trim();
              console.log('Extracted filename from unquoted match:', filename);
            } else {
              console.log('Could not extract filename from header');
            }
          }
        } else {
          console.log('No Content-Disposition header found');
        }
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(urlObj);
        document.body.removeChild(a);
      } else {
        console.error('Failed to download invoice');
      }
    } catch (err) {
      console.error('Error downloading invoice:', err);
    } finally {
      setDownloading(prev => ({ ...prev, [downloadKey]: false }));
    }
  };

  const isPackageLessThan14DaysOld = (pkg) => {
    if (!pkg.timestamp) return false;
    const purchaseDate = new Date(pkg.timestamp);
    const now = new Date();
    const daysSincePurchase = Math.floor((now - purchaseDate) / (1000 * 60 * 60 * 24));
    return daysSincePurchase <= 14;
  };

  const handleCancelPackage = async (packageId) => {
    if (!isAuthenticated() || !token) return;

    setCancelling(prev => ({ ...prev, [packageId]: true }));
    setShowCancelConfirmation(null);

    try {
      const response = await fetch(config.getApiUrl(`/api/purchase/annul/${packageId}`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Refresh packages list
        const packagesResponse = await fetch(config.getApiUrl('/api/purchase/purchased-packages'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (packagesResponse.ok) {
          const packagesData = await packagesResponse.json();
          setPackages(packagesData.packages || []);
        }
      } else {
        const errorText = await response.text();
        setError(errorText || 'Fehler beim Stornieren des Pakets');
      }
    } catch (err) {
      setError('Verbindungsfehler beim Stornieren des Pakets');
      console.error('Error cancelling package:', err);
    } finally {
      setCancelling(prev => ({ ...prev, [packageId]: false }));
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <ContentContainer>
          {renderSkeletonCards()}
        </ContentContainer>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <HeroSection>
        <HeroTitle>
          <PackageBadgeWithIcon />
          <MainTitle>Gekaufte Pakete</MainTitle>
        </HeroTitle>
        <Subtitle>Übersicht deiner Stundenpakete</Subtitle>
      </HeroSection>
      
      <ContentContainer>
        <BackButton onClick={() => navigate(getBackNavigationPath())}>
          ← Zurück zu {getBackButtonText()}
        </BackButton>

        {error && (
          <div style={{
            background: '#ffebee',
            color: '#c62828',
            border: '1px solid #ffcdd2',
            borderRadius: theme.borderRadius.medium,
            padding: '16px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {packages.length === 0 ? (
          <EmptyState>
            <EmptyStateIcon>📦</EmptyStateIcon>
            <EmptyStateText>Du hast noch keine Stundenpakete gekauft.</EmptyStateText>
          </EmptyState>
        ) : (
          <>
            {currentPackages.map((pkg, index) => {
            const status = getPackageStatus(pkg);
            const totalAvailable = getTotalAvailableLessons(pkg.availableLessonBudgets);
            
            const canCancel = isPackageLessThan14DaysOld(pkg) && !pkg.wasAnnuled && pkg.packageId !== 'trial-lesson' && !isUserAdmin();
            const showAnnulmentInvoice = pkg.wasAnnuled;
            
            return (
              <PackageCard key={pkg.id || index}>
                <PackageHeader>
                  <PackageName>{pkg.packageName || 'Stundenpaket'}</PackageName>
                  <ButtonGroup>
                    {/* Always show Rechnung button */}
                    <DownloadButton 
                        onClick={() => downloadInvoice(pkg.id, false)}
                        disabled={downloading[pkg.id]}
                      >
                        {downloading[pkg.id] ? (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17V7h2v7.17l3.59-3.59L17 10l-7 7z"/>
                            </svg>
                            Lade...
                          </>
                        ) : (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                            </svg>
                            Rechnung
                          </>
                        )}
                      </DownloadButton>
                    {/* Show Stornorechnung button only if annulled */}
                    {showAnnulmentInvoice && (
                      <DownloadButton 
                        onClick={() => downloadInvoice(pkg.id, true)}
                        disabled={downloading[`${pkg.id}_annulment`]}
                      >
                        {downloading[`${pkg.id}_annulment`] ? (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17V7h2v7.17l3.59-3.59L17 10l-7 7z"/>
                            </svg>
                            Lade...
                          </>
                        ) : (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                            </svg>
                            Stornorechnung
                          </>
                        )}
                      </DownloadButton>
                    )}
                    {/* Show Stornieren button only if not annulled and less than 14 days old */}
                    {canCancel && (
                      <CancelButton 
                        onClick={() => setShowCancelConfirmation(pkg.id)}
                        disabled={cancelling[pkg.id]}
                      >
                        {cancelling[pkg.id] ? (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17V7h2v7.17l3.59-3.59L17 10l-7 7z"/>
                            </svg>
                            Storniere...
                          </>
                        ) : (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                            </svg>
                            Stornieren
                          </>
                        )}
                      </CancelButton>
                    )}
                  </ButtonGroup>
                </PackageHeader>
                
                <PackageDetails>
                  <DetailItem>
                    <DetailLabel>Kaufdatum</DetailLabel>
                    <DetailValue>{formatDate(pkg.timestamp)}</DetailValue>
                  </DetailItem>
                  {isUserAdmin() && (
                    <DetailItem>
                      <DetailLabel>Benutzer</DetailLabel>
                      <DetailValue>
                        {pkg.displayUsername || pkg.username || 'N/A'}
                      </DetailValue>
                    </DetailItem>
                  )}
                  <DetailItem>
                    <DetailLabel>Verfügbare Stunden</DetailLabel>
                    <DetailValue>{totalAvailable} von {Object.values(pkg.lessonBudgets || {}).reduce((sum, budget) => sum + parseInt(budget || 0), 0)}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Preis</DetailLabel>
                    <DetailValue>{pkg.price || 'N/A'}</DetailValue>
                  </DetailItem>
                </PackageDetails>

                <div>
                  <DetailLabel style={{ marginBottom: '8px', display: 'block' }}>Verfügbare Stunden nach Typ:</DetailLabel>
                  <LessonsGrid>
                    {Object.entries(pkg.availableLessonBudgets || {}).map(([lessonType, amount]) => (
                      <LessonTypeCard key={lessonType} available={parseInt(amount)} category={getLessonCategory(lessonType)}>
                        <LessonTypeName available={parseInt(amount)} category={getLessonCategory(lessonType)}>
                          {getLessonTypeName(lessonType)}
                        </LessonTypeName>
                        <LessonTypeAmount available={parseInt(amount)} category={getLessonCategory(lessonType)}>
                          {amount} {parseInt(amount) === 1 ? 'Stunde' : 'Stunden'}
                        </LessonTypeAmount>
                      </LessonTypeCard>
                    ))}
                  </LessonsGrid>
                </div>
              </PackageCard>
            );
          })}
          
          {totalPages > 1 && (
            <PaginationContainer>
              <PaginationButton 
                onClick={handlePreviousPage} 
                disabled={currentPage === 1}
              >
                ←
              </PaginationButton>
              
              {Array.from({ length: totalPages }, (_, index) => index + 1).map(page => (
                <PaginationButton
                  key={page}
                  onClick={() => handlePageChange(page)}
                  active={currentPage === page}
                >
                  {page}
                </PaginationButton>
              ))}
              
              <PaginationButton 
                onClick={handleNextPage} 
                disabled={currentPage === totalPages}
              >
                →
              </PaginationButton>
            </PaginationContainer>
          )}
        </>
        )}
      </ContentContainer>

      {showCancelConfirmation && (
        <ConfirmationPopup
          isOpen={!!showCancelConfirmation}
          onClose={() => setShowCancelConfirmation(null)}
          onConfirm={() => handleCancelPackage(showCancelConfirmation)}
          title="Paket stornieren"
          message="Möchtest du dieses Stundenpaket wirklich stornieren? Alle aktiven Stunden, die zu diesem Paket gehören, werden ebenfalls storniert. Für verbleibendes Stundenbudget erhältst du eine Stornorechnung und eine Rückerstattung."
          confirmText="Ja, stornieren"
          cancelText="Abbrechen"
          isDestructive={true}
          isLoading={cancelling[showCancelConfirmation]}
        />
      )}
    </PageLayout>
  );
};

export default PurchasedPackagesPage; 